import { FC } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipContentProps,
} from 'recharts';
import { METRICAS, type PontoDre } from '@/utils/equiplexDre';
import { margemDoRotuloEmPe, type MedidasRotulo } from '@/utils/rotulosGrafico';
import { fmtBRL, fmtBRLCompacto, fmtPct, mesCurto } from '@/utils/painelFormat';
import AreaDoGrafico from '../AreaDoGrafico';
import { ALTURA_GRAFICO, FONTE_ROTULO, tickEixo, type CoresPainel } from '../chartTheme';
import Legenda from '../Legenda';
import PainelCard from '../PainelCard';
import { rotuloNoTopo } from '../rotuloBarra';
import TooltipBox from '../TooltipBox';

interface Props {
  serie: PontoDre[];
  metrica: string;
  onMetrica: (m: string) => void;
  mesSelecionado: string;
  cores: CoresPainel;
}

const SeletorMetrica: FC<{ metrica: string; onMetrica: (m: string) => void }> = ({ metrica, onMetrica }) => (
  <div role="group" aria-label="Métrica" className="flex flex-wrap gap-1.5">
    {METRICAS.map((m) => (
      <button
        key={m.key}
        type="button"
        aria-pressed={metrica === m.key}
        onClick={() => onMetrica(m.key)}
        className={`rounded-full border px-3 py-1 text-xs ${
          metrica === m.key
            ? 'border-accent bg-accent font-semibold text-accent-contrast'
            : 'border-theme-border text-content-secondary hover:text-content-primary'
        }`}
      >
        {m.rotulo}
      </button>
    ))}
  </div>
);

/** Uma coluna por mês com "−R$ 1,2 mi": deitado, ~96 px por mês; em pé, ~22 (03/11/2026). */
const MEDIDAS: MedidasRotulo = { reservado: 128, deitado: 96, emPe: 22 };

/**
 * Uma métrica da DRE mês a mês. Azul acima de zero, vermelho abaixo (direção, não
 * julgamento); mês com contábil incompleto fica esmaecido e o tooltip diz por quê. O valor fica
 * em cima da barra (embaixo, quando negativo).
 */
const ResultadoChart: FC<Props> = ({ serie, metrica, onMetrica, mesSelecionado, cores }) => {
  const rotulo = METRICAS.find((m) => m.key === metrica)?.rotulo ?? metrica;
  const barras = serie.map((p) => ({ ...p, rotulo: mesCurto(p.label) }));
  // Barra negativa leva o número embaixo dela: a régua ganha folga em pixels para ele não cair no mês.
  const temNegativo = barras.some((b) => b.valor < 0);
  // "R$ -912,3 mil" é largo: abaixo de ~84 px por mês o número fica só no tooltip.
  const topoEmPe = margemDoRotuloEmPe(barras.map((b) => fmtBRLCompacto(b.valor)), FONTE_ROTULO);

  const conteudoTooltip = ({ active, payload }: TooltipContentProps) => {
    const p = active && payload?.[0] ? (payload[0].payload as PontoDre) : null;
    if (!p) return null;
    const linhas = [{ rotulo, valor: fmtBRL(p.valor) }];
    if (p.margem !== null && metrica !== 'receita_liquida') linhas.push({ rotulo: '% da receita líquida', valor: fmtPct(p.margem) });
    return (
      <TooltipBox titulo={p.label} linhas={linhas} rodape={p.incompleto ? 'Contábil do mês ainda incompleto.' : undefined} />
    );
  };

  return (
    <PainelCard testId="dre-chart-resultado" titulo={`${rotulo} por mês`} subtitulo="Contábil do Datasul, mês civil." acoes={<SeletorMetrica metrica={metrica} onMetrica={onMetrica} />}>
      <Legenda
        itens={[
          { rotulo: 'Positivo', cor: cores.acima },
          { rotulo: 'Negativo', cor: cores.abaixo },
        ]}
      />
      <AreaDoGrafico altura={ALTURA_GRAFICO} categorias={barras.length} medidas={MEDIDAS}>
        {(modo) => (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={barras} margin={{ top: modo === 'em-pe' ? Math.max(topoEmPe, 24) : 24, right: 8, bottom: 0, left: 8 }} barCategoryGap="24%">
              <CartesianGrid vertical={false} stroke={cores.grade} />
              <XAxis dataKey="rotulo" tick={tickEixo(cores)} axisLine={{ stroke: cores.eixo }} tickLine={false} interval={0} />
              <YAxis
                tickFormatter={(v: number) => fmtBRLCompacto(v)}
                tick={tickEixo(cores)}
                axisLine={false}
                tickLine={false}
                width={112}
                padding={temNegativo ? { top: 0, bottom: 24 } : undefined}
              />
              <ReferenceLine y={0} stroke={cores.eixo} />
              <Tooltip content={conteudoTooltip} cursor={{ fill: cores.grade, fillOpacity: 0.35 }} />
              <Bar dataKey="valor" name={rotulo} radius={[4, 4, 4, 4]} maxBarSize={56} isAnimationActive={false}>
                {barras.map((b) => (
                  <Cell
                    key={b.key}
                    fill={b.valor >= 0 ? cores.acima : cores.abaixo}
                    fillOpacity={b.incompleto ? 0.45 : 1}
                    stroke={b.key === mesSelecionado ? cores.texto : undefined}
                    strokeWidth={b.key === mesSelecionado ? 1.5 : 0}
                  />
                ))}
                {modo && <LabelList dataKey="valor" content={rotuloNoTopo(fmtBRLCompacto, cores.texto, undefined, modo === 'em-pe')} />}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </AreaDoGrafico>
    </PainelCard>
  );
};

export default ResultadoChart;
